import { inject, Service } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { StorageService } from '../modules/checklist/services/storage.service';
import { ChecklistItem } from '../modules/checklist/types/ChecklistTypes';
import { findItem } from '../modules/checklist/utils/checklist-patches';
import { ChecklistHttpService } from './checklist-http.service';
import { ChecklistStoreService } from './checklist-store.service';

@Service()
export class ChecklistLoaderService {
  private readonly store = inject(ChecklistStoreService);
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly storageService = inject(StorageService);

  async loadChecklist(
    id: string,
    options: { skipIfGenerationStillAt?: number } = {}
  ): Promise<void> {
    const localPreview = this.store
      .checklists()
      .find(c => c.id === id && c.phase === 'Preview');
    if (localPreview) {
      return;
    }

    const shouldSkip = (): boolean =>
      options.skipIfGenerationStillAt !== undefined &&
      (this.store.writeGeneration(id) ?? 0) === options.skipIfGenerationStillAt;

    try {
      const checklist = await firstValueFrom(
        this.checklistHttpService.getChecklist(id)
      );
      if (shouldSkip()) {
        return;
      }

      this.store.merge(checklist);
    } catch (error) {
      console.error(`Failed to load checklist ${id}:`, error);

      const cachedChecklists =
        (await this.storageService.loadChecklists()) ?? [];
      const cachedChecklist = cachedChecklists.find(c => c.id === id);
      if (cachedChecklist && !shouldSkip()) {
        this.store.merge(cachedChecklist, { persist: false });
      }
    }
  }

  refreshChecklistAfterWrite(checklistId: string): void {
    void this.loadChecklist(checklistId, {
      skipIfGenerationStillAt: this.store.writeGeneration(checklistId),
    });
  }

  async fetchServerChecklistItem(
    checklistId: string,
    sectionId: string,
    itemId: string
  ): Promise<ChecklistItem | null> {
    try {
      const checklist = await firstValueFrom(
        this.checklistHttpService.getChecklist(checklistId)
      );
      return findItem(checklist, sectionId, itemId);
    } catch (error) {
      console.error(`Failed to fetch checklist ${checklistId}:`, error);
      return null;
    }
  }
}
